import { computed, ref, toValue } from 'vue'
import type { ComputedRef, MaybeRefOrGetter, Ref } from 'vue'

import Network from '@/Network'
import type { PostStationStationEvaluationResponse } from '@/Network/Types/Responses/PostStationResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'

import { isPostStationApprovedError } from '../errors'
import { isEvaluationApproved } from '../mappers'

export interface UsePostStationLock {
  /** OR entre a avaliação sondada e o 400 do backend. */
  isPostStationLocked: ComputedRef<boolean>
  /** Avaliação crua, para descer por prop e evitar um segundo GET do mesmo endpoint. */
  evaluation: Ref<PostStationStationEvaluationResponse | null>
  canReadPostStation: ComputedRef<boolean>
  /** Reseta e re-sonda. Idempotente e nunca lança. */
  refresh: () => Promise<void>
  /** `true` se o erro é o guard do backend; efeito colateral: trava a UI. */
  applyApprovedError: (error: unknown) => boolean
}

/**
 * O que o caller sabe do plantão para decidir se vale sondar a avaliação.
 *
 * `hasDoctor` indefinido = "não sei" ⇒ não bloqueia a sondagem; só `false`
 * explícito pula.
 */
export interface PostStationProbeEligibility {
  endHour?: string | Date | null
  hasDoctor?: boolean
}

/**
 * Só existe avaliação de pós-plantão para plantão JÁ encerrado e com médico: é
 * o Audit Bot que cria a linha, e só nessas condições (dec. 44/52 da iniciativa
 * dashboard-mobile-v2). Sondar fora disso é 404 garantido — 82% das sondagens em
 * PROD eram de plantões que ainda nem tinham começado.
 *
 * Comparação por instante: o `endHour` do backend é ISO com offset (ou `Date`),
 * então `getTime()` independe do fuso do navegador. Uma string sem offset seria
 * lida no fuso local, que é o do plantão (BRT) para os usuários do web.
 */
export function shouldProbePostStation(
  { endHour, hasDoctor }: PostStationProbeEligibility,
  now: Date = new Date()
): boolean {
  if (!endHour || hasDoctor === false) return false

  const end = new Date(endHour).getTime()
  return !Number.isNaN(end) && end < now.getTime()
}

/**
 * Trava de UI da RN-07 / RF-12 do PRD 14: plantão com avaliação de Pós-Plantão
 * Aprovada tem os dados críticos protegidos, e o ajuste passa obrigatoriamente
 * pela reversão justificada (US-06).
 *
 * Extraído de `stationDetailsModal.vue` e `update.station.modal.vue`, onde o
 * mesmo trio (gate de leitura + sondagem + estado da trava) estava duplicado
 * literalmente, antes de acrescentar novos pontos de bloqueio.
 *
 * Duas decisões deliberadas:
 *
 * 1. **Sem `watch` interno.** No `stationDetailsModal` a `scheduleId` sai de
 *    `stationDetails`, que só existe DEPOIS do `getStationDetails`. Um watch em
 *    `stationId` dispararia com a `scheduleId` ainda indefinida, o gate de
 *    leitura daria false, nenhuma sondagem aconteceria — e o teste "não busca
 *    sem permissão" continuaria verde enquanto a trava morria em produção. Quem
 *    sabe o momento certo é o caller; ele chama `refresh()`.
 * 2. **Sem cache.** O fluxo do PRD é "reverter a validação ⇒ volta editável", e
 *    a reversão acontece em outra rota da mesma sessão SPA. Cache por
 *    `stationId` deixaria o plantão travado depois de reverter.
 *
 * Sem flag, sem `scheduleId` ou sem `PostStationStationStatusReading`, nenhuma
 * request é feita e NADA trava. Isso é requisito, não efeito colateral: quem não
 * pode ler o status não pode ser bloqueado por falta de leitura — a barreira
 * real é o 400 `POST_STATION_EVALUATION_APPROVED` do backend.
 *
 * `eligibility` (opcional) evita o 404 esperado de plantão futuro, em andamento
 * ou sem médico — ver `shouldProbePostStation`. Sem ele (`null`/omitido), a
 * sondagem segue como antes. A guarda nunca afrouxa a barreira real: o 400 do
 * backend continua travando via `applyApprovedError`.
 */
export function usePostStationLock(
  stationId: MaybeRefOrGetter<number | null | undefined>,
  scheduleId: MaybeRefOrGetter<number | null | undefined>,
  eligibility: MaybeRefOrGetter<PostStationProbeEligibility | null | undefined> = null
): UsePostStationLock {
  const permissionsStore = usePermissionsStore()
  const { flags } = useFeatureFlags()

  const evaluation = ref<PostStationStationEvaluationResponse | null>(null)
  // Separado da avaliação: quando o backend recusa, a trava vale mesmo sem
  // sondagem (a varredura pode aprovar o plantão com o modal aberto).
  const lockedByServer = ref(false)

  const canReadPostStation = computed(() => {
    const currentScheduleId = toValue(scheduleId)
    if (!flags.enabledPostStation || !currentScheduleId) return false

    return permissionsStore.check({
      permission: AuthorizationPermissions.PostStationStationStatusReading,
      resource: {
        objectType: AuthorizationResourcesAndSubjects.Schedule,
        objectId: currentScheduleId
      }
    })
  })

  const isPostStationLocked = computed(
    () => lockedByServer.value || isEvaluationApproved(evaluation.value)
  )

  const refresh = async () => {
    evaluation.value = null

    const currentStationId = toValue(stationId)
    if (!canReadPostStation.value || !currentStationId) return

    const currentEligibility = toValue(eligibility)
    if (currentEligibility && !shouldProbePostStation(currentEligibility)) return

    try {
      // `null` = 404 = plantão sem avaliação (estado esperado) ⇒ nada travado.
      evaluation.value = await Network.postStation.getStationEvaluation(currentStationId)
    } catch (e) {
      // fallback intencional: falha de leitura não pode travar nem quebrar o
      // caller — é uma sondagem auxiliar, sem UI própria. O backend revalida a
      // mesma regra e devolve 400 (POST_STATION_EVALUATION_APPROVED), que o
      // call site exibe via `applyApprovedError`/`errors.ts`, então a barreira
      // real e a mensagem ao usuário continuam de pé.
      console.error('Falha ao verificar a trava de pós-plantão do plantão', e)
    }
  }

  const applyApprovedError = (error: unknown) => {
    if (!isPostStationApprovedError(error)) return false

    lockedByServer.value = true
    return true
  }

  return { isPostStationLocked, evaluation, canReadPostStation, refresh, applyApprovedError }
}
